import { useState } from "react";
import { getDeviceProfile, saveDeviceSender } from "../../lib/device-profile";
import StepHeader from "../../components/StepHeader";
import type { SenderDetails } from "@shared/types";

interface Props {
  initial: SenderDetails | null;
  onSubmit: (sender: SenderDetails) => void;
  onBack: () => void;
  submitting: boolean;
}

const UK_POSTCODE = /^[A-Za-z]{1,2}\d[A-Za-z\d]?\s?\d[A-Za-z]{2}$/;

// Step 5 — sender details. Pre-filled from this device's saved profile (see
// src/lib/device-profile.ts) when available, so a returning sender usually
// only has to check and confirm rather than retype everything. Only
// postcode is strictly required; the rest improves label accuracy
// (courier ContactName/Phone) and is requested, not demanded.
export default function SenderStep({ initial, onSubmit, onBack, submitting }: Props) {
  const remembered = getDeviceProfile()?.sender;
  const [name, setName] = useState(initial?.name ?? remembered?.name ?? "");
  const [postcode, setPostcode] = useState(initial?.postcode ?? remembered?.postcode ?? "");
  const [addressLine1, setAddressLine1] = useState(initial?.addressLine1 ?? remembered?.addressLine1 ?? "");
  const [addressLine2, setAddressLine2] = useState(initial?.addressLine2 ?? remembered?.addressLine2 ?? "");
  const [townCity, setTownCity] = useState(initial?.townCity ?? remembered?.townCity ?? "");
  const [email, setEmail] = useState(initial?.email ?? remembered?.email ?? "");
  const [mobile, setMobile] = useState(initial?.mobile ?? remembered?.mobile ?? "");
  const [error, setError] = useState<string | null>(null);

  function submit() {
    const trimmedPostcode = postcode.trim();
    if (!UK_POSTCODE.test(trimmedPostcode)) {
      setError("That doesn't look like a valid UK postcode.");
      return;
    }
    const sender: SenderDetails = {
      postcode: trimmedPostcode.toUpperCase(),
      name: name.trim() || undefined,
      addressLine1: addressLine1.trim() || undefined,
      addressLine2: addressLine2.trim() || undefined,
      townCity: townCity.trim() || undefined,
      email: email.trim() || undefined,
      mobile: mobile.trim() || undefined,
    };
    saveDeviceSender(sender);
    onSubmit(sender);
  }

  return (
    <div className="stack fade-in-up">
      <StepHeader title="Where are you sending from?" onBack={onBack} />
      <p style={{ color: "var(--color-text-muted)", margin: 0 }}>
        {remembered ? "We've filled this in from last time — check it's still right." : "We need these details for an accurate label and to reach you if there's a problem."}
      </p>

      <div className="card stack">
        <div className="field">
          <label htmlFor="name">Your name</label>
          <input id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Bruce Wayne" autoComplete="name" />
        </div>
        <div className="field">
          <label htmlFor="addressLine1">Address</label>
          <input id="addressLine1" value={addressLine1} onChange={(e) => setAddressLine1(e.target.value)} placeholder="House number and street" autoComplete="address-line1" />
        </div>
        <div className="field">
          <label htmlFor="addressLine2">Address line 2 (optional)</label>
          <input id="addressLine2" value={addressLine2} onChange={(e) => setAddressLine2(e.target.value)} autoComplete="address-line2" />
        </div>
        <div className="field">
          <label htmlFor="townCity">Town / city</label>
          <input id="townCity" value={townCity} onChange={(e) => setTownCity(e.target.value)} autoComplete="address-level2" />
        </div>
        <div className="field">
          <label htmlFor="postcode">Postcode</label>
          <input id="postcode" value={postcode} onChange={(e) => setPostcode(e.target.value)} placeholder="e.g. SW1A 1AA" autoComplete="postal-code" />
          {error && <span className="field-error">{error}</span>}
        </div>
        <div className="field">
          <label htmlFor="mobile">Mobile number</label>
          <span className="field-help">Some couriers need this to confirm collection or delivery.</span>
          <input id="mobile" type="tel" value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="07…" autoComplete="tel" />
        </div>
        <div className="field">
          <label htmlFor="email">Email (optional)</label>
          <input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="For your receipt and tracking" autoComplete="email" />
        </div>
      </div>

      <button className="btn btn-primary" onClick={submit} disabled={submitting}>
        {submitting ? "Saving…" : "Continue"}
      </button>
    </div>
  );
}
